import React, { useCallback, useRef, useMemo, useState } from "react"
import { useDispatch } from "react-redux"
import { Loader, Button, Box, Title } from "dash-styled"
import type { SchemaFormRefProps } from "resha"
import { updateBaselineData } from "../../../../../../../../Actions/API/AppInstance/AppInstanceBaselineDataActions"
import {
    getValue,
    hasValue
} from "../../../../../../../../Models/Client/FetchState"
import { useGroundTruth } from "../../../../../../../../Hooks/useGroundTruth"
import { AppDetailBase } from "../../../AppDetailBase/AppDetailBase"
import { push, goBack } from "connected-react-router"
import { routePaths } from "../../../../../../../../Routes"
import { useAppTemplate } from "../../../../../../../../Hooks/useAppTemplate"
import { Banner } from "../../../../../../../Common/Banner/Banner"
import { LabelingLayout } from "../../../../../../../Common/LabelingLayout/LabelingLayout"
import { useAppInstance } from "../../../../../../../../Hooks/useAppInstance"
import { DataAccess } from "../../../../../../../../Models/Client/DataAccess"
import { useUrlSigner } from "../../../../../../../../Hooks/useUrlSigner"
import { FilterAndSortExpression } from "../../../../../../../../Models/Client/FilterAndSortExpression"
import {
    APIGroundTruthModel,
    getSchemaFormattedFormData,
    getSchemaFormData
} from "../../../../../../../../Models/API/APIGroundTruthModel"
import { DataAccessContext } from "../../../../../../../../Context/DataAccessContext"
import { useMLActionProcessor } from "../../../../../../../../Hooks/useMLActionProcessor"
import { DataPointEditApp } from "../../../../../../../Common/Lazy/DataPointEditApp"
import { MainThunkDispatch } from "../../../../../../../../Reducers"
import { InputFormRender } from "../../../../../../../Common/InputFormRender/InputFormRender"
import noop from "lodash/noop"
import { useFetchState } from "../../../../../../../../Hooks/useFetchState"
import { useSchemaFormOutput } from "../../../../../../../../Hooks/useSchemaFormOutput"

interface BaselineDataProps {
    orgUsername: string
    instanceID: string
    baselineID: string
    filterAndSort: FilterAndSortExpression<APIGroundTruthModel>
}

export const AppCustomizeBaselineDataEdit = ({
    orgUsername,
    baselineID,
    instanceID,
    filterAndSort
}: BaselineDataProps) => {
    const dispatch = useDispatch<MainThunkDispatch>()
    const formRef = useRef<SchemaFormRefProps>(null)
    const groundTruthFetch = useGroundTruth(instanceID, baselineID)
    const instanceFetch = useAppInstance(instanceID)
    const templateFetch = useAppTemplate(instanceID)
    const [saving, setSaving] = useState<boolean>(false)
    const schemaFormDataFetch = useFetchState(
        groundTruthFetch,
        instanceFetch,
        templateFetch,
        getSchemaFormData
    )

    const [outputFormData, setOutputFormData] = useSchemaFormOutput(
        getValue(schemaFormDataFetch)?.output.formData
    )

    const annotationSchemaFormDataFetch = useFetchState(
        groundTruthFetch,
        instanceFetch,
        templateFetch,
        getSchemaFormattedFormData
    )

    const toolbarRef = useRef<HTMLDivElement>(null)
    const footerRef = useRef<HTMLDivElement>(null)
    const dataAccess = useMemo(() => DataAccess.perGT(baselineID), [baselineID])
    const urlSigner = useUrlSigner(dataAccess)

    const handleSubmit = useCallback(
        async (output: unknown) => {
            if (!hasValue(templateFetch) || !hasValue(instanceFetch)) {
                return
            }
            try {
                setSaving(true)
                await dispatch(
                    updateBaselineData(
                        instanceID,
                        baselineID,
                        output,
                        `ground-truth/${baselineID}/output`,
                        instanceFetch.value.outputSchema ??
                            templateFetch.value.outputSchema
                    )
                )
                dispatch(
                    push(
                        routePaths.appCustomizeBaselineDataDetail(
                            orgUsername,
                            instanceID,
                            baselineID,
                            filterAndSort
                        )
                    )
                )
            } finally {
                setSaving(false)
            }
        },
        [
            instanceFetch,
            templateFetch,
            dispatch,
            instanceID,
            baselineID,
            orgUsername,
            filterAndSort
        ]
    )
    const baseProps = {
        orgUsername,
        id: instanceID,
        title: `Edit ground-truth data: ${baselineID}`,
        breadcrumbs: ["Ground-truth Data"],
        fixedContentWidth: true
    }
    const handleBack = useCallback(() => dispatch(goBack()), [dispatch])
    const mlActionProcessor = useMLActionProcessor(dataAccess)

    if (
        !hasValue(instanceFetch) ||
        !hasValue(groundTruthFetch) ||
        !hasValue(templateFetch) ||
        !hasValue(schemaFormDataFetch) ||
        !hasValue(annotationSchemaFormDataFetch)
    ) {
        return (
            <AppDetailBase {...baseProps}>
                <Loader />
            </AppDetailBase>
        )
    }

    const editCapability =
        templateFetch.value.metadata.edit_output ?? "labeling"

    if (editCapability === "not_allowed") {
        return (
            <AppDetailBase {...baseProps}>
                <Banner icon="info">
                    This data program does not support editing baseline data.
                </Banner>
            </AppDetailBase>
        )
    }

    if (editCapability === "annotation") {
        return (
            <LabelingLayout
                orgUsername={orgUsername}
                mode="edit_groundtruth"
                groundTruthID={baselineID}
                instanceFetch={instanceFetch}
                onBack={handleBack}
                toolbar={<div ref={toolbarRef} />}
            >
                <DataPointEditApp
                    {...annotationSchemaFormDataFetch.value}
                    errorMessage="Error rendering ground-truth data"
                    urlSigner={urlSigner}
                    toolbarRef={toolbarRef}
                    footerRef={footerRef}
                    saveButtonLabel="Update"
                    resetAnnotation={false}
                    uploadPathPrefix={`ground-truth/${baselineID}/assets`}
                    mlActionProcessor={mlActionProcessor}
                    fallback={<Loader absolute={true} mask={false} />}
                    onSave={handleSubmit}
                />
            </LabelingLayout>
        )
    }

    return (
        <DataAccessContext.Provider value={dataAccess}>
            <AppDetailBase {...baseProps}>
                <Box marginBottom="5">
                    <Title variant="heading2" component="h3" weight="semibold">
                        Inputs
                    </Title>
                    <InputFormRender
                        {...schemaFormDataFetch.value.input}
                        formData={groundTruthFetch.value.input}
                        skipMissingDataField={true}
                    />
                </Box>
                <Box marginBottom="5">
                    <Title variant="heading2" component="h3" weight="semibold">
                        Outputs
                    </Title>
                    <InputFormRender
                        {...schemaFormDataFetch.value.output}
                        ref={formRef}
                        testId="ground-truth-edit-form"
                        onChange={setOutputFormData}
                        formData={outputFormData}
                        onSubmit={handleSubmit}
                        onError={noop}
                    />
                </Box>
                <Button
                    testId="submit-button"
                    color="darkviolet"
                    variant="contained"
                    onClick={() => formRef.current?.submit()}
                    disabled={saving}
                    className="w-64 mx-auto"
                >
                    {saving ? "Updating..." : "Update"}
                </Button>
            </AppDetailBase>
        </DataAccessContext.Provider>
    )
}
